import React, { Suspense, lazy, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import * as ph from './services/posthogClient'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ScoringConfigProvider } from './context/ScoringConfigContext'
import { ThemeProvider } from './context/ThemeContext'
import { AuthProvider } from './context/AuthContext'
import { FavoritesProvider } from './context/FavoritesContext'
import AuthErrorToast from './components/auth/AuthErrorToast'
import Footer from './components/common/Footer'
import { useAuth } from './context/AuthContext'
import './App.css'

const CHUNK_LOAD_RELOAD_KEY = '__snowfield_chunk_reload_attempted__'
const CHUNK_LOAD_ERROR_REGEX = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|ChunkLoadError|Cannot read properties of undefined \(reading 'default'\)/i

const getErrorMessage = (error) => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error?.message) return error.message
  return String(error)
}

const isChunkLoadError = (error) => CHUNK_LOAD_ERROR_REGEX.test(getErrorMessage(error))

/**
 * ScrollToTop component - scrolls to top on route changes
 * Only scrolls when navigating to a different page, not when
 * changing ski type within the same location
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathRef = React.useRef(pathname);

  useEffect(() => {
    // Extract base path without ski type suffix
    const getBasePath = (path) => {
      // Remove ski type suffixes (/backcountry, /heli) from location routes
      return path.replace(/\/(backcountry|heli)$/, '') || '/';
    };

    const prevBase = getBasePath(prevPathRef.current);
    const currentBase = getBasePath(pathname);

    // Only scroll to top if the base path actually changed
    if (prevBase !== currentBase) {
      window.scrollTo(0, 0);
    }

    prevPathRef.current = pathname;

    // Fire PostHog pageview on every route change (SPA navigation)
    ph.capture('$pageview', { $current_url: window.location.href });
  }, [pathname]);

  return null;
}

// Lazy load main routes to reduce initial bundle size
const WeatherApp = lazy(() => import('./WeatherApp'))
const ActivityForecast = lazy(() => import('./ActivityForecast'))
const ResortComparison = lazy(() => import('./pages/ResortComparison'))
const ResortHealthDashboard = lazy(() => import('./pages/ResortHealthDashboard'))
const FavoritesOverview = lazy(() => import('./pages/FavoritesOverview'))
const RegionOverview = lazy(() => import('./pages/RegionOverview'))
const NotFound = lazy(() => import('./pages/NotFound'))

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, retryCount: 0, isChunkLoadError: false }
  }
  componentDidMount() {
    if (typeof window === 'undefined') return
    // Clear stale reload flags so future deploys can self-heal once again.
    if (sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === '1') {
      setTimeout(() => sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY), 15000)
    }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunkLoadError: isChunkLoadError(error) }
  }
  componentDidCatch(error, errorInfo) {
    const chunkLoadFailure = isChunkLoadError(error)
    console.error('ErrorBoundary caught:', error, errorInfo?.componentStack)

    if (chunkLoadFailure) {
      if (typeof window === 'undefined') return

      const hasRetried = sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === '1'
      if (!hasRetried) {
        sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, '1')
        window.location.reload()
        return
      }

      // Reload was already attempted for this tab; show fallback UI instead of looping.
      return
    }

    Sentry.captureException(error, {
      tags: {
        boundary: 'root',
      },
      extra: { componentStack: errorInfo?.componentStack },
    })
  }
  handleRetry = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY)
    }
    this.setState(prev => ({ hasError: false, error: null, retryCount: prev.retryCount + 1, isChunkLoadError: false }))
  }
  handleReload = () => {
    window.location.reload()
  }
  handleGoHome = () => {
    window.location.href = '/'
  }
  render() {
    if (this.state.hasError) {
      const isProd = import.meta.env.PROD
      const fallbackMessage = this.state.isChunkLoadError
        ? 'The app updated while this tab was open. Reload to get the latest version.'
        : isProd
          ? 'The page hit an unexpected error. Try reloading.'
          : this.state.error?.message
      return (
        <div style={{ padding: '40px 20px', maxWidth: 480, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⛷️</div>
          <h1 style={{ fontSize: 22, color: 'var(--text-primary)', margin: '0 0 8px' }}>Something went wrong</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 24px' }}>
            {fallbackMessage}
          </p>
          {!isProd && <pre style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-disabled)', overflow: 'auto', maxHeight: 200, marginBottom: 16 }}>{this.state.error?.stack}</pre>}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={this.handleRetry} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: 'var(--brand-primary)', color: 'var(--text-inverse)', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              Try Again
            </button>
            <button onClick={this.handleReload} style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>
              Reload Page
            </button>
            <button onClick={this.handleGoHome} style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>
              Go Home
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Isolated error boundary for health dashboard - won't affect main app
const HealthDashboardWrapper = () => (
  <ErrorBoundary>
    <Suspense fallback={<div style={{ padding: 20 }}>Loading health dashboard...</div>}>
      <ResortHealthDashboard />
    </Suspense>
  </ErrorBoundary>
)

/**
 * Pre-fills the UserDispatch feedback widget's email field when the user is logged in.
 * The widget doesn't have a native pre-fill API, so we use a MutationObserver to detect
 * when the form renders and inject the value + fire an input event for internal state sync.
 */
function UserDispatchPrefill() {
  const { user } = useAuth();
  const observerRef = useRef(null);

  useEffect(() => {
    if (!user?.email) return;

    const fill = (input) => {
      if (input.value) return; // already has a value — don't overwrite user edits
      // Use the native setter to bypass React's synthetic event system
      const nativeSet = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype, 'value'
      ).set;
      nativeSet.call(input, user.email);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const getInput = () => {
      // The widget renders inside a shadow DOM — regular querySelector can't penetrate it
      const host = document.querySelector('#userdispatch-widget');
      return host?.shadowRoot?.querySelector('[data-field="email"]') ?? null;
    };

    observerRef.current = new MutationObserver(() => {
      const input = getInput();
      if (input) fill(input);
    });

    // Also observe mutations inside the shadow root itself
    const host = document.querySelector('#userdispatch-widget');
    if (host?.shadowRoot) {
      observerRef.current.observe(host.shadowRoot, { childList: true, subtree: true });
    }
    observerRef.current.observe(document.body, { childList: true, subtree: true });

    // Try immediately in case the widget is already open
    const input = getInput();
    if (input) fill(input);

    return () => observerRef.current?.disconnect();
  }, [user?.email]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <UserDispatchPrefill />
          <AuthErrorToast />
          <FavoritesProvider>
            <ScoringConfigProvider>
              <BrowserRouter>
                <ScrollToTop />
                {/* Skip link for keyboard navigation */}
                <a href="#main-content" className="skip-link">
                  Skip to main content
                </a>
                <Suspense fallback={<div style={{ padding: 20 }}>Loading...</div>}>
                  <main id="main-content" role="main">
                  <Routes>
                    <Route path="/" element={<ResortComparison />} />
                    <Route path="/backcountry" element={<ResortComparison />} />
                    <Route path="/heli" element={<ResortComparison />} />
                    <Route path="/activities" element={<ActivityForecast />} />
                    <Route path="/monitoring" element={<HealthDashboardWrapper />} />
                    <Route path="/explore" element={<Navigate to="/" replace />} />
                    <Route path="/explore/backcountry" element={<Navigate to="/backcountry" replace />} />
                    <Route path="/explore/heli" element={<Navigate to="/heli" replace />} />
                    <Route path="/favorites" element={<FavoritesOverview />} />
                    <Route path="/region/:regionSlug" element={<RegionOverview />} />
                    <Route path="/:locationId/:skiType?" element={<WeatherApp />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </main>
                </Suspense>
                <Footer />
              </BrowserRouter>
            </ScoringConfigProvider>
          </FavoritesProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
